import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { GlobalContext } from "../../context/GlobalContext";
import steps, {
  getStepComponent,
  getStepFootNote,
  getStepLabel,
  getStepSidebarNote,
} from "./steps.config";
import useStepNavigation from "./useStepNavigation";
import ThanksComponent from "../questionscomponent/subcomponent/ThanksComponent";
import img1 from "../../Images/logo2.webp";
import CustomLogoImage from "../shared/CustomLogoImage";
import AppSnackbar from "../shared/AppSnackbar";
import axios from "../../api/axios";
import { getForwardableQueryString } from "./resumeApplication";
import "./stepper.css";

// Continue lifts the front card away and drops the next one in from below;
// Back reverses it — the previous card drops back down from above. Direction
// is which way that lift/drop goes, not which way anything slides sideways.
const CARD_TRANSITION = { duration: 0.32, ease: [0.4, 0, 0.2, 1] };
const cardVariants = {
  enter: (direction) => ({ opacity: 0, y: direction >= 0 ? 28 : -28, scale: 0.98 }),
  center: { opacity: 1, y: 0, scale: 1 },
  exit: (direction) => ({ opacity: 0, y: direction >= 0 ? -28 : 28, scale: 0.98 }),
};

// The current-step pill in the step nav sits its label text directly on top
// of the dynamic account color, which can be anything from near-white to
// near-black (this session's test account is literally #000000) — so the
// text color has to be picked for contrast rather than fixed. Standard
// WCAG-ish relative-luminance check; falls back to the dark brand text if
// the color string can't be parsed (e.g. a named color or unexpected format).
function contrastTextColor(color) {
  if (!color) return null;
  let r, g, b;
  const hexMatch = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else if (rgbMatch) {
    [, r, g, b] = rgbMatch.map(Number);
  } else {
    return "#0e303e";
  }
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#0e303e" : "#ffffff";
}

/**
 * PD-6 step shell.
 *
 * Top-level container for the new 5-step application. Renders the current
 * step (resolved from steps.config based on business/personal path), a
 * single sidebar card combining the "Step N of 5" progress indicator with
 * a "Your deal so far" summary (facts only — no rate/payment numbers), and
 * a "Save & finish later" action. Reuses GlobalContext/GlobalState untouched.
 */
const StepShell = ({ initialStepIndex = 0 }) => {
  const { state } = useContext(GlobalContext);
  const nav = useStepNavigation(initialStepIndex);
  const [submitted, setSubmitted] = useState(false);
  const [saveForLaterStatus, setSaveForLaterStatus] = useState(null); // null | "sending" | "sent" | "error"
  const [saveForLaterError, setSaveForLaterError] = useState("");
  // Imperative handle onto whichever step component is currently mounted
  // (StepYou/StepBusiness/etc. — see the forwardRef + useImperativeHandle
  // each of them exposes) so "Save & finish later" can run that step's own
  // validation first. Only actually needed on StepYou (index 0): the
  // save-for-later email is sent to whatever's in `state.questions.email`,
  // which is only captured on that step — every later step gates on the
  // same field being present already, so re-validating the *current* step's
  // own unrelated fields there would block a save the backend can already
  // fulfill (email's on file), just to demand answers the borrower hasn't
  // reached yet.
  const activeStepRef = useRef(null);

  const handleSaveForLater = async () => {
    if (!state?.applicationUUID) return;
    if (nav.stepIndex === 0 && activeStepRef.current?.validateForSaveLater?.() === false) return;
    setSaveForLaterStatus("sending");
    setSaveForLaterError("");
    try {
      // Forwarded so the emailed resume link keeps the original third-party
      // params — the Equipment prefill and "Your deal so far" read them
      // from the URL.
      await axios.post(
        `lender/partial-application/save-for-later/?uuid=${state.applicationUUID}`,
        { query_params: getForwardableQueryString(window.location.search) }
      );
      setSaveForLaterStatus("sent");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setSaveForLaterError(
        detail === "Applicant email not found"
          ? "Add your email above first — that's where we'll send your link."
          : "Something went wrong sending your link. Please try again."
      );
      setSaveForLaterStatus("error");
    }
  };

  // Stable reference on purpose: AppSnackbar re-arms its autoHideDuration
  // timer whenever this prop's identity changes, and this app re-renders far
  // more often than a user would notice (an unrelated pre-existing issue
  // elsewhere in the tree) — an inline arrow function here would reset the
  // 5s countdown on nearly every render and the snackbar would never
  // auto-dismiss.
  const handleCloseSaveForLaterSnackbar = useCallback(() => {
    setSaveForLaterStatus(null);
  }, []);

  // useStepNavigation only exposes goNext/goBack/goToStep, not which way the
  // index just moved — derive it by comparing against the previous render's
  // index, so AnimatePresence knows whether the new card should rise from
  // below (Continue) or drop from above (Back/jumping to a done step).
  const prevStepIndexRef = useRef(nav.stepIndex);
  const direction = nav.stepIndex >= prevStepIndexRef.current ? 1 : -1;
  useEffect(() => {
    prevStepIndexRef.current = nav.stepIndex;
  }, [nav.stepIndex]);

  // `state.isPersonalFlow` is a server-side permission flag ("this
  // company/link is allowed to run the personal path"), not the borrower's
  // actual choice — same split the old pre-PD-6 flow used (Ssn.js/
  // companyname.js only route through its EquipmentUse.js screen when this
  // is true). The borrower's real pick lives in
  // `questions.equipmentUse` ("Business"/"Personal"), captured by the new
  // "How will you use the equipment?" question at the end of StepYou.js —
  // which itself only appears when this flag is true. A company that isn't
  // flagged for the personal path never shows that question and always
  // gets the business flow, regardless of what's sitting in `equipmentUse`.
  const isPersonalFlow =
    Boolean(state?.isPersonalFlow) && state?.questions?.equipmentUse === "Personal";
  const StepComponent = getStepComponent(nav.currentStep, isPersonalFlow);
  const stepLabel = getStepLabel(nav.currentStep, isPersonalFlow);
  const sidebarNote = getStepSidebarNote(nav.currentStep, isPersonalFlow);
  const stepNavItems = steps.map((step, index) => ({
    index,
    label: getStepLabel(step, isPersonalFlow),
  }));

  // The tenant/dealer's brand color, fetched per-application in question.js
  // (`lender/validate-slug/` -> theme_color) or per-account in AppWrapper.js.
  // Overriding --paid-primary here — rather than hardcoding a color — is
  // what makes every chip/step-indicator/slider/CTA in this shell pick up
  // that account color instead of the design reference's fixed lime accent.
  // Mirrors the old per-question flow's own logo fallback (Email.js, deleted
  // by PD-18): state.user.logo is the dealer/company's own white-label
  // branding, populated by question.js's validate-slug lookup for whichever
  // link the borrower landed on (marketing-partner links included, e.g.
  // Boom and Bucket/Soarr each being their own tenant company) — falls back
  // to PAID's own bundled logo when that company has none set.
  const brandLogoSrc = state?.user?.logo || img1;
  const themeColor = state?.user?.themeColor;
  const shellStyle = themeColor
    ? { "--paid-primary": themeColor, "--paid-primary-contrast": contrastTextColor(themeColor) }
    : undefined;

  const equipmentDescription =
    state?.questions?.equipmentName ||
    [
      state?.questions?.personal?.financingYear,
      state?.questions?.personal?.financingMake,
      state?.questions?.personal?.financingModel,
    ]
      .filter(Boolean)
      .join(" ");

  const amountRequested = state?.questions?.equipmentCost || state?.questions?.requestedAmount;

  const businessOrApplicantName = isPersonalFlow
    ? [state?.questions?.firstname, state?.questions?.lastname].filter(Boolean).join(" ")
    : state?.questions?.business?.companyName;

  // StepEquipment.js's chips store "Replacement"/"Expansion" (the latter is
  // the existing `purchase_type` backend contract) — the sidebar just needs
  // a plainer word for "adding to the fleet".
  const purchaseTypeLabel = {
    Replacement: "Replacement",
    Expansion: "Addition",
  }[state?.questions?.business?.purchaseType];

  // No progress to show while still on the first step — the bar only
  // starts moving once the borrower actually advances past it.
  const progressPercent = Math.round((nav.stepIndex / steps.length) * 100);

  if (submitted) {
    return (
      <div className="pd6-shell" style={shellStyle}>
        <div className="pd6-shell__topbar">
          <div className="pd6-shell__brand">
            <CustomLogoImage src={brandLogoSrc} className="pd6-shell__brand-img" alt="PAID" />
            <span className="pd6-shell__brand-divider" />
            <span className="pd6-shell__app-title">Equipment financing application</span>
          </div>
          <div className="pd6-shell__topbar-actions">
            <span className="pd6-shell__saved-indicator">Saved just now</span>
            <button type="button" className="pd6-btn pd6-btn--ghost pd6-btn--small" disabled>
              Save &amp; finish later
            </button>
          </div>
        </div>
        <div className="pd6-shell__progress">
          <div className="pd6-shell__progress-fill" style={{ width: "100%" }} />
        </div>
        <div className="pd6-shell__body">
          <div className="pd6-card-stack">
            <div className="pd6-shell__main">
              <ThanksComponent />
            </div>
          </div>
          <aside className="pd6-shell__aside">
            <div className="pd6-progress-card">
              <div className="pd6-progress-card__eyebrow">Progress</div>
              <div className="pd6-progress-card__headline">All done</div>
              <div className="pd6-progress-card__track">
                <div className="pd6-progress-card__fill" style={{ width: "100%" }} />
              </div>
              <p className="pd6-progress-card__hint">Your application is with our team.</p>
            </div>
            <div className="pd6-trust-callout">
              <svg
                className="pd6-trust-callout__icon"
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="4" y="11" width="16" height="10" rx="2.5" />
                <path d="M7.5 11V7.5a4.5 4.5 0 0 1 9 0V11" />
              </svg>
              <p>Your signed copy is stored securely. Only you and your funding specialist can see it.</p>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="pd6-shell" style={shellStyle}>
      <div className="pd6-shell__topbar">
        <div className="pd6-shell__brand">
          <CustomLogoImage
            src={brandLogoSrc}
            className="pd6-shell__brand-img"
            alt="PAID"
          />
          <span className="pd6-shell__brand-divider" />
          <span className="pd6-shell__app-title">Equipment financing application</span>
        </div>
        <div className="pd6-shell__topbar-actions">
          <span className="pd6-shell__saved-indicator">Saved just now</span>
          <button
            type="button"
            className="pd6-btn pd6-btn--ghost pd6-btn--small"
            onClick={handleSaveForLater}
            disabled={saveForLaterStatus === "sending"}
          >
            {saveForLaterStatus === "sending" ? "Sending…" : "Save & finish later"}
          </button>
        </div>
      </div>
      <AppSnackbar
        open={saveForLaterStatus === "sent" || saveForLaterStatus === "error"}
        onClose={handleCloseSaveForLaterSnackbar}
        severity={saveForLaterStatus === "error" ? "error" : "success"}
        message={
          saveForLaterStatus === "error"
            ? saveForLaterError
            : "Check your email — we sent you a link to pick up right where you left off."
        }
      />
      <div className="pd6-shell__progress">
        <div
          className="pd6-shell__progress-fill"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <nav className="pd6-step-nav" aria-label="Application steps">
        {stepNavItems.map((item) => {
          const status =
            item.index < nav.stepIndex
              ? "done"
              : item.index === nav.stepIndex
              ? "current"
              : "upcoming";
          const clickable = status === "done";
          return (
            <button
              key={item.index}
              type="button"
              className={`pd6-step-nav__item pd6-step-nav__item--${status}`}
              onClick={clickable ? () => nav.goToStep(item.index) : undefined}
              disabled={!clickable}
            >
              <span className="pd6-step-nav__index">
                {status === "done" ? "✓" : item.index + 1}
              </span>
              <span className="pd6-step-nav__label">{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="pd6-shell__body">
        <div className="pd6-card-stack">
          {/* V3 has no decorative stacked-cards-behind effect — just the
           * single flat front card, no time-left badge either. */}
          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.div
              className="pd6-shell__main"
              key={nav.stepIndex}
              custom={direction}
              variants={cardVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={CARD_TRANSITION}
            >
              <div className="pd6-step__eyebrow-row">
                <div className="pd6-step__eyebrow">
                  Step {nav.stepIndex + 1} of {steps.length}
                </div>
              </div>
              <StepComponent
                ref={activeStepRef}
                goNext={nav.goNext}
                goBack={nav.goBack}
                goToStep={nav.goToStep}
                isFirstStep={nav.isFirstStep}
                isLastStep={nav.isLastStep}
                label={stepLabel}
                footNote={getStepFootNote(nav.currentStep, isPersonalFlow)}
                onSubmitted={() => setSubmitted(true)}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        <aside className="pd6-shell__aside">
          <div className="pd6-progress-card">
            <div className="pd6-progress-card__eyebrow">Progress</div>
            <div className="pd6-progress-card__headline">
              Step {nav.stepIndex + 1} of {steps.length}
            </div>
            <div className="pd6-progress-card__track">
              <div
                className="pd6-progress-card__fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="pd6-progress-card__hint">
              {nav.stepIndex + 1 < steps.length
                ? `Next: ${getStepLabel(steps[nav.stepIndex + 1], isPersonalFlow)}.`
                : "Last step. Sign and you're finished."}
            </p>
          </div>

          <div className="pd6-deal-summary">
            <h5>Your deal so far</h5>
            <div className="pd6-deal-summary__row">
              <span>Equipment</span>
              <strong>{equipmentDescription || "—"}</strong>
            </div>
            <div className="pd6-deal-summary__row">
              <span>Price</span>
              <strong>
                {amountRequested ? `$${parseInt(amountRequested)?.toLocaleString()}` : "—"}
              </strong>
            </div>
            <div className="pd6-deal-summary__row">
              <span>{isPersonalFlow ? "Applicant" : "Business"}</span>
              <strong>{businessOrApplicantName || "—"}</strong>
            </div>
            <div className="pd6-deal-summary__row">
              <span>Purchase type</span>
              <strong>{purchaseTypeLabel || "—"}</strong>
            </div>
          </div>

          {sidebarNote && (
            <div className="pd6-trust-callout">
              <svg
                className="pd6-trust-callout__icon"
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="4" y="11" width="16" height="10" rx="2.5" />
                <path d="M7.5 11V7.5a4.5 4.5 0 0 1 9 0V11" />
              </svg>
              <p>{sidebarNote}</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default StepShell;
export { steps };
